import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, LogOut, ChevronRight, Users } from 'lucide-react';
import { AppState } from '../../types';
import { GroupId, StartingPeriod } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { ProfileSection } from './ProfileSection';
import { AccountsState } from '../../types/accounts';
import { AccountSwitcher } from './AccountSwitcher';
import { useAppStore } from '../../hooks/useAppStore';
interface SettingsPageProps {
  state: AppState;
  selectedGroup: GroupId;
  startingPeriod: StartingPeriod;
  manualPeriod?: StartingPeriod | null;
  onSetStartingPeriod: (period: StartingPeriod | null) => void;
  onUpdateName?: (newName: string) => void;
  accounts?: AccountsState;
  onSwitchAccount?: (accountId: string) => void;
  onLogout?: () => void;
}
type SettingsTab = 'profile' | 'accounts';
const tabs: {
  id: SettingsTab;
  label: string;
  icon: React.ElementType;
}[] = [{
  id: 'profile',
  label: 'Profiel',
  icon: User
}, {
  id: 'accounts',
  label: 'Accounts',
  icon: Users
}];
export function SettingsPage({
  state,
  selectedGroup,
  startingPeriod,
  manualPeriod,
  onSetStartingPeriod,
  onUpdateName,
  accounts,
  onSwitchAccount,
  onLogout
}: SettingsPageProps) {
  const {
    viewingLearnerId
  } = useAppStore();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const accountsList = accounts ? Object.values(accounts.accountsById) : [];
  const activeAccount = accounts ? accounts.accountsById[accounts.activeAccountId] : null;
  // Determine if we are viewing a learner (as parent/teacher)
  const isViewingLearner = !!viewingLearnerId && (activeAccount?.role === 'parent' || activeAccount?.role === 'teacher');
  // Display Name Logic:
  // - If viewing learner: Use learner's name (from state.stats.name)
  // - If NOT viewing learner (Parent/Teacher): Use activeAccount.displayName
  // - If Learner account: Use activeAccount.displayName (or state.stats.name, they should match)
  const displayName = isViewingLearner ? state.stats.name : activeAccount?.displayName || state.stats.name;
  const accountRole = activeAccount?.role || 'learner';
  return <div className="max-w-5xl mx-auto" data-id="element-5028">
      {/* Header */}
      <div className="mb-10" data-id="element-5029">
        <h1 className="text-4xl font-black text-slate-900" data-id="element-5030">Instellingen</h1>
        <p className="text-slate-600 mt-2 text-lg font-semibold" data-id="element-5031">
          Beheer je profiel en instellingen
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-id="element-5032">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-3" data-id="element-5033">
          <Card className="p-3 sticky top-8" data-id="element-5034">
            <nav className="space-y-2" data-id="element-5035">
              {tabs.map(tab => {
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                      w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200 relative min-h-[56px]
                      ${isActive ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900 font-semibold'}
                    `} data-id="element-5036">
                    <div className="flex items-center gap-4" data-id="element-5037">
                      <tab.icon className={`w-6 h-6 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-5038" />
                      <span className="text-base" data-id="element-5039">{tab.label}</span>
                    </div>
                    {isActive && <motion.div layoutId="settings-tab-indicator" data-id="element-5040">
                        <ChevronRight className="w-5 h-5 text-indigo-400" data-id="element-5041" />
                      </motion.div>}
                  </button>;
            })}
            </nav>

            <div className="mt-5 pt-5 border-t border-slate-100 px-2" data-id="element-5042">
              <button onClick={onLogout} className="w-full flex items-center gap-4 p-4 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors text-base font-semibold min-h-[56px]" data-id="element-5043">
                <LogOut className="w-5 h-5" data-id="element-5044" />
                Uitloggen
              </button>
            </div>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-9" data-id="element-5045">
          <AnimatePresence mode="wait" data-id="element-5046">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.2,
            ease: 'easeOut'
          }} data-id="element-5047">
              {activeTab === 'profile' && <ProfileSection stats={state.stats} displayName={displayName} selectedGroup={selectedGroup} startingPeriod={startingPeriod} manualPeriod={manualPeriod} onSetStartingPeriod={onSetStartingPeriod} onUpdateName={onUpdateName} accountRole={accountRole} isViewingLearner={isViewingLearner} data-id="element-5048" />}

              {activeTab === 'accounts' && <Card className="p-8" data-id="element-5049">
                  <div className="mb-8" data-id="element-5050">
                    <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-5051">
                      Account beheer
                    </h2>
                    <p className="text-slate-600 text-lg font-semibold" data-id="element-5052">
                      Schakel tussen je leerling-, ouder-, docent- en
                      beheerderaccounts.
                    </p>
                  </div>

                  {accounts && onSwitchAccount ? <AccountSwitcher accounts={accountsList} activeAccountId={accounts.activeAccountId} onSwitchAccount={onSwitchAccount} data-id="element-5053" /> : <div className="text-center py-12 text-slate-600 text-lg" data-id="element-5054">
                      Account beheer is niet beschikbaar
                    </div>}
                </Card>}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>;
}